import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Mail, Phone, MapPin, Lock } from 'lucide-react';
import Logo from './Logo';
import { postNewsletter } from '../lib/api';
import { useSettings } from '../lib/SettingsContext';
import { useToast } from '../hooks/use-toast';

import { useLang } from '../lib/i18n';
const NAV_LINKS = [
  { label: 'Inicio', path: '/' },
  { label: 'Quiénes somos', path: '/quienes-somos' },
  { label: 'Qué ofrecemos', path: '/que-ofrecemos' },
  { label: 'Experiencias', path: '/experiencias' },
  { label: 'Noticias', path: '/noticias' },
  { label: 'Colaboradores', path: '/colaboradores' },
  { label: 'Contacto', path: '/contacto' },
];

export default function Footer() {
  const { ui } = useLang();
  const { settings } = useSettings();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await postNewsletter(email);
      toast({ title: '¡Bienvenido a Golf Divino!', description: `Te hemos suscrito con ${email}.` });
      setEmail('');
    } catch (_) {
      toast({ title: 'Error', description: 'No se pudo suscribir. Inténtalo de nuevo.', variant: 'destructive' });
    }
    setSending(false);
  };

  return (
    <footer className="bg-[#0a0a0b] border-t border-white/5 mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4 flex flex-col gap-5">
          <Logo size="md" />
          <p className="text-white/60 text-[15px] leading-relaxed mt-2">Donde los negocios crecen y las experiencias perduran. El club empresarial de los amantes del golf y el vino.</p>
          <div className="flex items-center gap-4 mt-2">
            {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[#c9a063] hover:text-[#c9a063] text-white/70 transition-colors"><Instagram size={15} /></a>}
            {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[#c9a063] hover:text-[#c9a063] text-white/70 transition-colors"><Facebook size={15} /></a>}
            {settings.social_linkedin && <a href={settings.social_linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[#c9a063] hover:text-[#c9a063] text-white/70 transition-colors"><Linkedin size={15} /></a>}
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="gd-eyebrow mb-4">{ui('footer.nav')}</div>
          <ul className="flex flex-col gap-2.5">
            {NAV_LINKS.map((l) => (<li key={l.path}><Link to={l.path} className="text-white/70 hover:text-[#c9a063] transition-colors text-[15px]">{l.label}</Link></li>))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="gd-eyebrow mb-4">{ui('footer.contact')}</div>
          <ul className="flex flex-col gap-3 text-white/70 text-[15px]">
            <li className="flex items-start gap-2.5"><Phone size={15} className="mt-1 text-[#c9a063]" /><a href={`tel:${settings.contact_phone}`} className="hover:text-[#c9a063]">{settings.contact_phone}</a></li>
            <li className="flex items-start gap-2.5"><Mail size={15} className="mt-1 text-[#c9a063]" /><a href={`mailto:${settings.contact_email}`} className="hover:text-[#c9a063]">{settings.contact_email}</a></li>
            <li className="flex items-start gap-2.5"><MapPin size={15} className="mt-1 text-[#c9a063]" />{settings.contact_address}</li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="gd-eyebrow mb-4">{ui('footer.newsletter')}</div>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={ui('form.your_email')} className="bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none transition-colors" />
            <button disabled={sending} type="submit" className="gd-btn-gold py-2.5 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium disabled:opacity-60">{sending ? 'Enviando...' : 'Suscribirme'}</button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center md:items-center justify-start gap-4 text-xs text-white/40 font-sans-ui tracking-wider">
          <div className="flex items-center gap-4 flex-wrap">
            <span>© {new Date().getFullYear()} Golf Divino · Business Club.</span>
            <Link to="/admin" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1 border border-white/15 hover:border-[#c9a063] hover:text-[#c9a063] transition-colors uppercase tracking-widest text-[10px]" title="Acceso administración (nueva pestaña)">
              <Lock size={11} /> Administración
            </Link>
            <Link to="/aviso-legal" className="hover:text-[#c9a063] transition-colors">{ui('footer.legal')}</Link>
            <Link to="/privacidad" className="hover:text-[#c9a063] transition-colors">{ui('footer.privacy')}</Link>
            <Link to="/cookies" className="hover:text-[#c9a063] transition-colors">{ui('footer.cookies')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
